import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
    Box,
    CircularProgress,
    IconButton,
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { SnackbarError } from "../../../components/base/SnackbarAlert/SnackbarError";
import { SnackbarSuccess } from "../../../components/base/SnackbarAlert/SnackbarSuccess";
import LoaderWrapper from "../../../components/wrapper/LoaderWrapper";
import {
    useCreateProductTypeMutation,
    useDeleteProductTypeMutation,
    useProductTypes,
} from "../../../services/productsService";

export function ProductTypesManagementTab() {
    const { t } = useTranslation();

    const { data: productTypes = [], isLoading, refetch } = useProductTypes();

    const [newProductType, setNewProductType] = useState({
        name: "",
        name_eng: "",
        no_footage: false,
    });

    const { mutate: createProductType, isPending: isCreating } = useCreateProductTypeMutation({
        onSuccess: () => {
            toast.custom(() => (
                <SnackbarSuccess
                    message={t("products.SalesInvestmentPage.productTypeCreatedSuccessfully")}
                />
            ));
            setNewProductType({
                name: "",
                name_eng: "",
                no_footage: false,
            });
            refetch();
        },
        onError: (error) => {
            console.error("Error creating product type:", error);
            toast.custom(() => (
                <SnackbarError
                    message={t("products.SalesInvestmentPage.failedToCreateProductType")}
                />
            ));
        },
    });

    const { mutate: deleteProductType } = useDeleteProductTypeMutation({
        onSuccess: () => {
            toast.custom(() => (
                <SnackbarSuccess
                    message={t("products.SalesInvestmentPage.productTypeDeletedSuccessfully")}
                />
            ));
            refetch();
        },
        onError: (error) => {
            console.error("Error deleting product type:", error);
            toast.custom(() => (
                <SnackbarError
                    message={t("products.SalesInvestmentPage.failedToDeleteProductType")}
                />
            ));
        },
    });

    const handleCreateProductType = () => {
        if (!newProductType.name.trim()) {
            toast.custom(() => (
                <SnackbarError
                    message={t("products.SalesInvestmentPage.productTypeNameRequired")}
                />
            ));
            return;
        }

        if (!newProductType.name_eng.trim()) {
            toast.custom(() => (
                <SnackbarError
                    message={t("products.SalesInvestmentPage.productTypeNameEngRequired")}
                />
            ));
            return;
        }

        createProductType(newProductType);
    };

    const handleDeleteProductType = (typeId, typeName) => {
        if (
            window.confirm(
                t("products.SalesInvestmentPage.confirmDeleteProductType", {
                    name: typeName,
                }),
            )
        ) {
            deleteProductType({ typeId });
        }
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    const tableComponent = () => (
        <TableContainer component={Paper} variant="outlined">
            <Table size="small">
                <TableHead>
                    <TableRow
                        sx={{
                            backgroundColor: (theme) => theme.palette.primary.main,
                            height: 56,
                        }}
                    >
                        <TableCell
                            sx={{
                                width: 230,
                                minWidth: 230,
                                color: (theme) => theme.palette.primary.contrastText,
                                fontSize: "1.15rem",
                                fontWeight: 500,
                            }}
                        >
                            {t("products.SalesInvestmentPage.productTypeName")}
                        </TableCell>
                        <TableCell
                            sx={{
                                width: 230,
                                minWidth: 230,
                                color: (theme) => theme.palette.primary.contrastText,
                                fontSize: "1.15rem",
                                fontWeight: 500,
                            }}
                        >
                            {t("products.SalesInvestmentPage.productTypeNameEng")}
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{
                                width: 150,
                                minWidth: 150,
                                color: (theme) => theme.palette.primary.contrastText,
                                fontSize: "1.15rem",
                                fontWeight: 500,
                            }}
                        >
                            {t("products.SalesInvestmentPage.noFootage")}
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{
                                width: 100,
                                minWidth: 100,
                                color: (theme) => theme.palette.primary.contrastText,
                                fontSize: "1.15rem",
                                fontWeight: 500,
                            }}
                        >
                            {t("products.SalesInvestmentPage.actions")}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {productTypes.map((productType) => (
                        <TableRow key={productType.id} sx={{ height: 48 }}>
                            <TableCell
                                sx={{
                                    width: 230,
                                    minWidth: 230,
                                    fontSize: "1.15rem",
                                    fontWeight: 400,
                                    py: 2,
                                }}
                            >
                                {productType.name}
                            </TableCell>
                            <TableCell
                                sx={{
                                    width: 230,
                                    minWidth: 230,
                                    fontSize: "1.15rem",
                                    fontWeight: 400,
                                    py: 2,
                                }}
                            >
                                {productType.name_eng}
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{
                                    width: 150,
                                    minWidth: 150,
                                    fontSize: "1.15rem",
                                    fontWeight: 400,
                                    py: 2,
                                    textTransform: "capitalize",
                                }}
                            >
                                {productType.no_footage ? t("yes") : t("no")}
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{
                                    width: 100,
                                    minWidth: 100,
                                    fontSize: "1.15rem",
                                    fontWeight: 400,
                                    py: 2,
                                }}
                            >
                                <IconButton
                                    color="error"
                                    size="small"
                                    onClick={() =>
                                        handleDeleteProductType(productType.id, productType.name)
                                    }
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </TableCell>
                        </TableRow>
                    ))}
                    <TableRow sx={{ height: 48 }}>
                        <TableCell
                            sx={{
                                width: 230,
                                minWidth: 230,
                                fontSize: "1.15rem",
                                fontWeight: 400,
                                py: 2,
                            }}
                        >
                            <TextField
                                fullWidth
                                size="small"
                                required
                                value={newProductType.name}
                                onChange={(e) =>
                                    setNewProductType({
                                        ...newProductType,
                                        name: e.target.value,
                                    })
                                }
                                placeholder={t(
                                    "products.SalesInvestmentPage.productTypeNamePlaceholder",
                                )}
                                inputProps={{ style: { fontSize: "1.15rem" } }}
                            />
                        </TableCell>
                        <TableCell
                            sx={{
                                width: 230,
                                minWidth: 230,
                                fontSize: "1.15rem",
                                fontWeight: 400,
                                py: 2,
                            }}
                        >
                            <TextField
                                fullWidth
                                size="small"
                                required
                                value={newProductType.name_eng}
                                onChange={(e) =>
                                    setNewProductType({
                                        ...newProductType,
                                        name_eng: e.target.value,
                                    })
                                }
                                placeholder={t(
                                    "products.SalesInvestmentPage.productTypeNameEngPlaceholder",
                                )}
                                inputProps={{ style: { fontSize: "1.15rem" } }}
                            />
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{
                                width: 150,
                                minWidth: 150,
                                fontSize: "1.15rem",
                                fontWeight: 400,
                                py: 2,
                            }}
                        >
                            <Switch
                                checked={newProductType.no_footage}
                                onChange={(e) =>
                                    setNewProductType({
                                        ...newProductType,
                                        no_footage: e.target.checked,
                                    })
                                }
                                size="small"
                            />
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{
                                width: 100,
                                minWidth: 100,
                                fontSize: "1.15rem",
                                fontWeight: 400,
                                py: 2,
                            }}
                        >
                            <IconButton
                                sx={{
                                    color: (theme) => theme.palette.success.main,
                                }}
                                size="small"
                                onClick={handleCreateProductType}
                                disabled={
                                    isCreating ||
                                    !newProductType.name.trim() ||
                                    !newProductType.name_eng.trim()
                                }
                            >
                                {isCreating ? <CircularProgress size={20} /> : <AddIcon />}
                            </IconButton>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
            {productTypes.length === 0 && (
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="body1" color="grey.600" sx={{ fontSize: "1.1rem" }}>
                        {t("products.SalesInvestmentPage.noProductTypesFound")}
                    </Typography>
                </Box>
            )}
        </TableContainer>
    );

    return (
        <Box sx={{ p: 2 }}>
            <LoaderWrapper showLoader={isLoading}>{tableComponent()}</LoaderWrapper>
        </Box>
    );
}
